import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { setCommitteeScope } from '../lib/committeeScope'

interface CommitteeScopeContextValue {
  activeCommitteeId: string | undefined
  setActiveCommitteeId: (id?: string) => void
}

const CommitteeScopeContext = createContext<CommitteeScopeContextValue>({
  activeCommitteeId: undefined,
  setActiveCommitteeId: () => {},
})

export function CommitteeScopeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [activeCommitteeId, setActiveCommitteeIdState] = useState<string | undefined>()

  const setActiveCommitteeId = (id?: string) => {
    setActiveCommitteeIdState(id)
    setCommitteeScope(id)
    // Switching the acting committee changes WHICH committee's rows every scoped read
    // returns (the axios interceptor appends `?committeeScope=` to future requests). Cached
    // list/dashboard queries whose keys don't fold in the active committee would otherwise
    // keep showing the PREVIOUS committee's rows until their staleTime elapsed -- the parent's
    // campaigns/scripts/lists staying visible while impersonating a child, even though the
    // server correctly rejects the by-id detail read. Clear the cache so every scoped read
    // refetches under the new scope; this is robust to any hook that forgets to key on the
    // active committee, rather than whack-a-mole per query key.
    queryClient.clear()
  }

  // Sync on mount (in case of page refresh)
  useEffect(() => {
    setCommitteeScope(activeCommitteeId)
  }, [activeCommitteeId])

  return (
    <CommitteeScopeContext.Provider value={{ activeCommitteeId, setActiveCommitteeId }}>
      {children}
    </CommitteeScopeContext.Provider>
  )
}

export function useCommitteeScopeContext() {
  return useContext(CommitteeScopeContext)
}
